import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { buildSeo } from "@/lib/seo";
import en from "@/i18n/resources/en";
import { Markdown } from "@/components/TwinChat";
import { askDemoTwin } from "@/lib/twin-check.functions";
import { ArrowRight, ArrowUpRight, Check, Copy, Minus, Plus, Search, Terminal } from "lucide-react";
import {
  Eyebrow,
  MarketingLayout,
  SectionHeader,
  useHydratedTranslation,
} from "@/components/marketing/Layout";
import { LogoMark } from "@/components/marketing/Logo";

// FAQ structured data reads the SAME source as the visible FAQ. They used to be
// two hand-maintained copies, which is how the schema ended up promising things
// (A/B testing, private models) the visible page no longer claimed. Search
// engines penalise FAQ markup that doesn't match the page, so this must be one
// source. `head()` runs outside React, so we read the English resource directly.
const HOMEPAGE_FAQS: { q: string; a: string }[] = en.home.faq.items;

export const Route = createFileRoute("/")({
  head: () => {
    const seo = buildSeo({
      path: "/",
      title: "Reality Twin — verified company layer for AI agents",
      description:
        "Make your company reachable for customers, partners and AI agents. Verified identity, answers in ChatGPT & Claude, Swiss-built. Free tier.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Reality Twin",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "The verified company layer for the AI-agent web: your company becomes an addressable, answering, verified entity — reachable by customers, partners and other companies' AI agents, and directly available in ChatGPT and Claude.",
            url: "https://realitytwin.io",
            publisher: { "@type": "Organization", name: "Ruznic Marketing" },
            offers: { "@type": "Offer", price: "0", priceCurrency: "CHF" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: HOMEPAGE_FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: Landing,
});

/* ============================== HERO ============================== */
/** The aha-moment, in front of the signup wall: type a website, see how AI
 *  actually reads that company, then ask its twin — no account. The strategy
 *  review calls this the single change that matters most, because the value
 *  arrives before the registration, not after it. */
function HeroTwinCheck() {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const v = url.trim();
        if (v) navigate({ to: "/twin-check", search: { url: v } });
      }}
      className="mt-8 mx-auto max-w-xl animate-fade-up"
      style={{ animationDelay: "220ms" }}
    >
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t("home.checkPlaceholder", { defaultValue: "yourcompany.com" })}
            aria-label={t("home.checkAria", { defaultValue: "Your website address" })}
            className="w-full rounded-full hairline bg-card pl-10 pr-4 py-3 text-[15px] focus:outline-none focus:border-foreground/30"
          />
        </div>
        <button
          type="submit"
          disabled={!url.trim()}
          className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 disabled:opacity-50"
        >
          {t("home.checkCta", { defaultValue: "See how AI reads you" })}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>
      <p className="mt-2.5 text-center text-[12.5px] text-muted-foreground">
        {t("home.checkHint", {
          defaultValue: "Free, no account — then ask that company's Twin anything.",
        })}
      </p>
    </form>
  );
}

/** The one proof that is live today and that no SMB competitor ships: the twin
 *  is reachable from inside ChatGPT and Claude. The review found this buried on
 *  a footer page — it belongs on the homepage with a copyable address. */
function ReachableInAI() {
  const t = useHydratedTranslation();
  const [copied, setCopied] = useState(false);
  const endpoint = "https://realitytwin.io/mcp";

  return (
    <section className="border-y border-border bg-cream py-20 sm:py-24">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <Eyebrow>
          <span className="inline-flex items-center gap-1.5">
            <Terminal className="h-3.5 w-3.5" />
            {t("home.mcp.eyebrow", { defaultValue: "Live today" })}
          </span>
        </Eyebrow>
        <h2 className="mt-4 font-serif text-[34px] sm:text-[48px] leading-[1.03] tracking-[-0.03em]">
          {t("home.mcp.title", {
            defaultValue: "Your company, reachable inside ChatGPT and Claude.",
          })}
        </h2>
        <p className="mt-5 text-[16.5px] leading-[1.6] text-muted-foreground">
          {t("home.mcp.body", {
            defaultValue:
              "Decision-makers research in AI assistants now, not search engines. Connect this address once and your twin answers them there — no website visit, no click, no search result.",
          })}
        </p>

        <div className="mt-7 flex items-center gap-2 max-w-md mx-auto">
          <code className="flex-1 min-w-0 truncate rounded-full hairline bg-background px-4 py-2.5 text-[13.5px] font-mono text-left">
            {endpoint}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(endpoint).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-[13.5px] font-medium text-background hover:opacity-90"
          >
            <Copy className="h-3.5 w-3.5" />
            {copied
              ? t("home.mcp.copied", { defaultValue: "Copied" })
              : t("home.mcp.copy", { defaultValue: "Copy" })}
          </button>
        </div>

        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Link
            to="/connect"
            className="inline-flex items-center gap-1.5 rounded-full hairline bg-background px-4 py-2 text-[13.5px] hover:bg-card transition"
          >
            {t("home.mcp.how", { defaultValue: "How to connect" })}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
          {/* Point humans at the explanation, not the raw JSON — the machine
              endpoint is linked from there for the people who want it. */}
          <Link
            to="/docs/mcp-server"
            className="inline-flex items-center gap-1.5 rounded-full hairline bg-background px-4 py-2 text-[13.5px] hover:bg-card transition"
          >
            {t("home.mcp.docs", { defaultValue: "Read the docs" })}
          </Link>
        </div>
      </div>
    </section>
  );
}

function Hero() {
  const t = useHydratedTranslation();
  return (
    <section className="relative overflow-hidden pt-16 sm:pt-24 pb-10">
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="flex justify-center animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full hairline bg-card px-3 py-1 text-[12px] text-muted-foreground">
            {t("home.badge")}
          </span>
        </div>

        <h1
          className="mt-8 text-center font-serif leading-[0.95] tracking-[-0.035em] text-[44px] sm:text-[80px] md:text-[104px] animate-fade-up"
          style={{ animationDelay: "50ms" }}
        >
          {t("home.heroTitle")}
          <br />
          <em className="italic text-muted-foreground">{t("home.heroTitleEm")}</em>
        </h1>

        <p
          className="mx-auto mt-8 max-w-xl text-center text-[16px] sm:text-[17px] leading-[1.55] text-muted-foreground px-2 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          {t("home.heroSub")}
        </p>

        <div
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-2.5 animate-fade-up"
          style={{ animationDelay: "180ms" }}
        >
          <Link
            to="/auth"
            className="group inline-flex w-full sm:w-auto justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition"
          >
            {t("home.ctaPrimary")}{" "}
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex w-full sm:w-auto justify-center items-center gap-1.5 rounded-full hairline bg-card px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
          >
            {t("home.ctaSecondary")}
          </Link>
        </div>

        <HeroTwinCheck />

        <HeroProductShot />
      </div>
    </section>
  );
}

/* ============================ PRODUCT SHOT ============================ */
function HeroProductShot() {
  const MOCK_W = 1100;
  const MOCK_H = 620;
  return (
    <div
      className="relative mx-auto mt-16 sm:mt-20 w-full max-w-[1100px] animate-fade-up"
      style={{ animationDelay: "260ms" }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: `${MOCK_W} / ${MOCK_H}` }}
      >
        <div
          className="absolute top-0 left-0 origin-top-left scale-[0.4] sm:scale-[0.58] md:scale-[0.78] lg:scale-100"
          style={{ width: `${MOCK_W}px`, height: `${MOCK_H}px` }}
        >
          <WindowFrame url="realitytwin.io / dashboard">
            <DashboardMock />
          </WindowFrame>
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 -bottom-1 h-24 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}

function WindowFrame({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div className="hairline rounded-[14px] bg-card overflow-hidden shadow-window h-full flex flex-col">
      <div className="flex items-center gap-2 px-4 h-9 border-b border-border bg-cream shrink-0">
        <div className="flex gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="mx-auto rounded-md bg-background hairline px-3 py-0.5 text-[11px] text-muted-foreground font-mono">
          {url}
        </div>
        <div className="w-12" />
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function DashboardMock() {
  return (
    <div className="grid grid-cols-[220px_1fr] h-full text-[13px]">
      <aside className="border-r border-border bg-cream/40 p-3.5">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-foreground text-background">
            <LogoMark size={12} className="!text-background" />
          </div>
          <div className="text-[13px] font-medium">Reality Twin</div>
          <ArrowUpRight className="ml-auto h-3 w-3 text-muted-foreground" />
        </div>
        <div className="mt-5">
          <div className="px-2 font-mono-eyebrow!text-[10px]">Workspace</div>
          <ul className="mt-1.5 space-y-0.5">
            {[
              ["Overview", false],
              ["Conversations", true],
              ["Knowledge", false],
              ["Leads", false],
              ["Opportunities", false],
              ["Marketplace", false],
            ].map(([label, active]) => (
              <li
                key={label as string}
                className={`flex items-center justify-between px-2 py-1.5 rounded-md ${active ? "bg-background hairline text-foreground" : "text-muted-foreground"}`}
              >
                <span>{label}</span>
                {label === "Conversations" && (
                  <span className="text-[10px] font-mono text-muted-foreground">12</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <div className="p-8 overflow-hidden">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono-eyebrow">Product preview</div>
            <div className="mt-1.5 font-serif text-[32px] leading-none truncate">
              Your workspace.
            </div>
          </div>
          <button className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-foreground px-3.5 py-1.5 text-[12px] font-medium text-background">
            Train twin
          </button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {[
            // Illustrative UI only — no invented traction numbers. The review is
            // explicit: fabricated evidence is the most damaging signal a trust
            // product can send, so the shape is shown, not fictional results.
            { k: "Conversations", v: "—", d: "" },
            { k: "Qualified leads", v: "—", d: "" },
            { k: "Avg. response", v: "—", d: "" },
          ].map((m) => (
            <div key={m.k} className="hairline rounded-lg p-3.5 min-w-0">
              <div className="text-[11px] text-muted-foreground truncate">{m.k}</div>
              <div className="mt-1 flex items-baseline gap-2 min-w-0">
                <div className="font-serif text-[26px] leading-none">{m.v}</div>
                <div className="text-[11px] text-muted-foreground shrink-0">{m.d}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 hairline rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="text-[12px] font-medium">Recent conversations</div>
            <div className="text-[11px] text-muted-foreground font-mono">example</div>
          </div>
          <table className="w-full text-[12.5px] table-fixed">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "48%" }} />
              <col className="hidden sm:table-column" style={{ width: "18%" }} />
              <col className="hidden sm:table-column" style={{ width: "12%" }} />
            </colgroup>
            <tbody>
              {[
                // Neutral placeholders, not invented customer names.
                ["Visitor", "Asked about integrations", "Lead", "—"],
                ["Visitor", "Partnership enquiry", "Opportunity", "—"],
                ["Visitor", "Asked about pricing", "Lead", "—"],
                ["Lumen Labs", "Demo request", "Lead · 88", "22m ago"],
                ["Quanta", "Investor — Series A", "Lead · 95", "31m ago"],
              ].map((row, i) => (
                <tr key={i} className="border-t border-border/70 first:border-t-0">
                  <td className="px-4 py-2.5 font-medium truncate">{row[0]}</td>
                  <td className="px-4 py-2.5 text-muted-foreground truncate">{row[1]}</td>
                  <td className="px-4 py-2.5 hidden sm:table-cell">
                    <span className="rounded-full hairline px-2 py-0.5 text-[11px] whitespace-nowrap">
                      {row[2]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-muted-foreground font-mono text-[11px] whitespace-nowrap hidden sm:table-cell">
                    {row[3]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============================ WHAT IS RTN ============================ */
function WhatIsRTN() {
  const t = useHydratedTranslation();
  return (
    <section id="what" className="border-y border-border py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
        <div>
          <Eyebrow>{t("home.whatIs.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.025em]">
            {t("home.whatIs.titleA")}{" "}
            <em className="italic text-muted-foreground">{t("home.whatIs.titleEm")}</em>
          </h2>
          <Link
            to="/platform"
            className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-[13.5px] font-medium text-background hover:opacity-90 transition"
          >
            {t("home.whatIs.cta")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="space-y-5 text-[16px] sm:text-[17px] leading-[1.65] text-muted-foreground">
          <p>{t("home.whatIs.p1")}</p>
          <p>{t("home.whatIs.p2")}</p>
          <p className="text-foreground">{t("home.whatIs.p3")}</p>
        </div>
      </div>
    </section>
  );
}

/* ============================ LOGO STRIP ============================ */
function LogoStrip() {
  const t = useHydratedTranslation();
  return (
    <section className="border-b border-border py-14">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <p className="font-mono-eyebrow">{t("home.logoStripEyebrow")}</p>
        <p className="mt-4 text-[15px] sm:text-[16px] leading-[1.6] text-muted-foreground">
          {t("home.logoStripBody")}
        </p>
      </div>
    </section>
  );
}

/* ============================ HOW IT WORKS ============================ */
function HowItWorks() {
  const t = useHydratedTranslation();
  const steps = t("home.how.steps", { returnObjects: true }) as { title: string; desc: string }[];
  return (
    <section id="how" className="bg-cream border-y border-border py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeader
          eyebrow={t("home.how.eyebrow")}
          title={
            <>
              {t("home.how.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.how.titleEm")}</em>
            </>
          }
        />
        <div className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border rounded-xl overflow-hidden">
          {steps.map((s, i) => (
            <div key={s.title} className="bg-background p-6 flex flex-col min-h-[180px]">
              <div className="font-mono-eyebrow">STEP {String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-6 font-serif text-[22px] leading-tight">{s.title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ FEATURES ============================ */
function Features() {
  const t = useHydratedTranslation();
  const feats = t("home.features.items", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];
  return (
    <section id="platform" className="py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeader
          eyebrow={t("home.features.eyebrow")}
          title={
            <>
              {t("home.features.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.features.titleEm")}</em>
            </>
          }
          sub={t("home.features.sub")}
        />
        <div className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
          {feats.map((f, i) => (
            <div key={f.title} className="bg-card p-7 flex flex-col min-h-[200px]">
              <div className="font-mono-eyebrow">0{i + 1}</div>
              <h3 className="mt-5 font-serif text-[24px] leading-tight">{f.title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ LIVE DEMO ============================ */
/** A real conversation with our own twin, grounded on realitytwin.io — replacing
 *  the scripted mock-up the strategy review flagged. Invented evidence is the
 *  most damaging signal a trust product can send, and the old script even
 *  claimed native CRM sync, which our own roadmap lists as planned. */
function RealTwinDemo() {
  const t = useHydratedTranslation();
  const ask = useServerFn(askDemoTwin);
  const [msgs, setMsgs] = useState<{ who: "visitor" | "twin"; text: string }[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const suggestions = [
    t("home.live.q1", { defaultValue: "What makes Reality Twin different?" }),
    t("home.live.q2", { defaultValue: "What does it cost?" }),
    t("home.live.q3", { defaultValue: "How long does setup take?" }),
  ];

  async function run(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setFailed(false);
    setMsgs((m) => [...m, { who: "visitor", text: q }]);
    setInput("");
    try {
      const r = await ask({ data: { url: "https://realitytwin.io", question: q } });
      if (r.ok && r.answer) setMsgs((m) => [...m, { who: "twin", text: r.answer }]);
      else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex-1 flex flex-col gap-4 overflow-y-auto">
        {msgs.length === 0 && !busy && (
          <div className="m-auto text-center px-4">
            <p className="text-[13.5px] text-muted-foreground">
              {t("home.live.prompt", {
                defaultValue:
                  "This is our own twin, answering live from this website. Ask it anything.",
              })}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => run(s)}
                  className="rounded-full hairline bg-background px-3 py-1.5 text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-cream transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <Bubble key={i} who={m.who} text={m.text} />
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="hairline bg-background rounded-2xl px-4 py-2.5 text-[13px] text-muted-foreground">
              …
            </div>
          </div>
        )}
        {failed && (
          <p className="text-[12.5px] text-muted-foreground text-center">
            {t("home.live.failed", {
              defaultValue: "The twin couldn't answer that right now — try another question.",
            })}
          </p>
        )}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(input);
        }}
        className="mt-auto hairline rounded-xl flex items-center gap-2 px-3.5 py-2.5 bg-background"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-label={t("home.live.placeholder", { defaultValue: "Ask the twin anything…" })}
          placeholder={t("home.live.placeholder", { defaultValue: "Ask the twin anything…" })}
          className="flex-1 bg-transparent text-[13px] placeholder:text-muted-foreground/60 focus:outline-none min-w-0"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          aria-label={t("home.live.send", { defaultValue: "Send message" })}
          className="shrink-0 rounded-lg bg-foreground px-2.5 py-1 text-[11px] font-medium text-background disabled:opacity-40"
        >
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>

      </form>
    </>
  );
}

function LiveDemo() {
  const t = useHydratedTranslation();
  return (
    <section id="demo" className="bg-cream border-y border-border py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeader
          eyebrow={t("home.live.eyebrow")}
          title={
            <>
              {t("home.live.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.live.titleEm")}</em>
            </>
          }
          sub={t("home.live.sub")}
        />
        <div className="mt-14 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div className="hairline rounded-[14px] bg-card overflow-hidden shadow-window">
            <div className="flex items-center gap-2 px-4 h-9 border-b border-border bg-background">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
              </div>
              <div className="mx-auto rounded-md bg-cream hairline px-3 py-0.5 text-[11px] text-muted-foreground font-mono">
                realitytwin.io / chat
              </div>
              <div className="w-12" />
            </div>
            <div className="p-6 sm:p-7 min-h-[420px] flex flex-col gap-4">
              <RealTwinDemo />
            </div>
          </div>
          <div className="hairline rounded-[14px] bg-card p-7">
            <div className="font-mono-eyebrow">{t("home.live.indexEyebrow")}</div>
            <h3 className="mt-2 font-serif text-[28px] leading-tight">
              {t("home.live.indexTitle")}
            </h3>
            {/* What the twin indexes — capabilities, not invented counts. The
                strategy review is explicit that fabricated metrics are the most
                damaging signal a trust product can send, so this states what
                happens rather than pretending to show someone's numbers. */}
            <ul className="mt-6 divide-y divide-border">
              {[
                t("home.live.k1", { defaultValue: "Every public page of your website" }),
                t("home.live.k2", {
                  defaultValue: "Documents you upload (PDF, docs, price lists)",
                }),
                t("home.live.k3", { defaultValue: "Answers cited back to their source" }),
                t("home.live.k4", {
                  defaultValue: "Re-crawled on a schedule, so it stays current",
                }),
                t("home.live.k5", {
                  defaultValue: "Questions it couldn't answer, reported to you",
                }),
              ].map((label) => (
                <li key={label} className="flex items-start gap-2.5 py-3 text-[14px]">
                  <Check className="h-3.5 w-3.5 mt-1 shrink-0" strokeWidth={2.5} />
                  <span>{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 hairline rounded-lg p-4">
              <div className="font-mono-eyebrow">{t("home.live.autoEyebrow")}</div>
              <div className="mt-1 font-serif text-[18px] leading-snug">
                {t("home.live.autoTitleHonest", {
                  defaultValue: "Your twin looks for matches while you sleep",
                })}
              </div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">
                {t("home.live.autoBodyHonest", {
                  defaultValue:
                    "It scans the network daily and surfaces companies worth talking to — you decide who gets an introduction.",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function Bubble({ who, text }: { who: "twin" | "visitor"; text: string }) {
  const isTwin = who === "twin";
  return (
    <div className={`flex ${isTwin ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${isTwin ? "bg-cream text-foreground" : "bg-foreground text-background"}`}
      >
        {/* The twin answers in markdown, so **bold** and lists must be
            rendered — otherwise the raw asterisks show up in the bubble.
            Visitor text is their own typing and stays literal. */}
        {isTwin ? <Markdown text={text} sources={[]} /> : text}
      </div>
    </div>
  );
}

/* ============================ USE CASES ============================ */
function UseCases() {
  const t = useHydratedTranslation();
  const cases = t("home.cases.items", { returnObjects: true }) as { title: string; desc: string }[];
  return (
    <section id="customers" className="py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeader
          eyebrow={t("home.cases.eyebrow")}
          title={
            <>
              {t("home.cases.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.cases.titleEm")}</em>
            </>
          }
        />
        <div className="mt-14 grid md:grid-cols-2 gap-px bg-border hairline rounded-xl overflow-hidden">
          {cases.map((c, i) => (
            <div key={c.title} className="bg-card p-6 sm:p-8 flex gap-5 sm:gap-6">
              <div className="font-serif text-[44px] leading-none text-muted-foreground/50 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="min-w-0">
                <h3 className="font-serif text-[22px] sm:text-[24px] leading-tight">{c.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ CUSTOMER OUTCOMES ============================ */
function Testimonials() {
  const t = useHydratedTranslation();
  return (
    <section className="bg-cream border-y border-border py-24 sm:py-28">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <Eyebrow>{t("home.partners.eyebrow")}</Eyebrow>
        <h2 className="mt-4 font-serif text-[36px] sm:text-[52px] leading-[1.05] tracking-[-0.025em]">
          {t("home.partners.titleA")}{" "}
          <em className="italic text-muted-foreground">{t("home.partners.titleEm")}</em>
        </h2>
        <p className="mt-6 text-[16px] leading-[1.6] text-muted-foreground">
          {t("home.partners.body")}
        </p>
        <Link
          to="/contact"
          className="mt-8 inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition"
        >
          {t("home.partners.cta")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}

/* ============================ PRICING ============================ */
function PricingPreview() {
  const t = useHydratedTranslation();
  // Single source of truth: read the exact same tier definitions as the
  // /pricing page (routes.pricing.tiers) so the two can never drift apart —
  // and the homepage prices become localized for free. Pro (index 2) is
  // highlighted as the featured plan.
  const tiers = (
    t("routes.pricing.tiers", { returnObjects: true }) as {
      name: string;
      price: string;
      desc: string;
      feats: string[];
      cta: string;
    }[]
  ).map((tier, i) => ({ ...tier, featured: i === 2 }));
  return (
    <section id="pricing-preview" className="py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeader
          eyebrow={t("home.pricing.eyebrow")}
          title={
            <>
              {t("home.pricing.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.pricing.titleEm")}</em>
            </>
          }
          sub={t("home.pricing.sub")}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border hairline rounded-xl overflow-hidden">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative bg-card p-7 flex flex-col ${tier.featured ? "bg-cream" : ""}`}
            >
              {tier.featured && (
                <div className="absolute top-5 right-5 font-mono-eyebrow!text-[10px] text-foreground">
                  {t("home.pricing.popular")}
                </div>
              )}
              <div className="text-[13px] text-muted-foreground">{tier.name}</div>
              <div className="mt-3 font-serif text-[44px] leading-none">
                {tier.price}
                {tier.price !== "CHF 0" && (
                  <span className="text-[14px] text-muted-foreground font-sans">
                    {t("home.pricing.perMo")}
                  </span>
                )}
              </div>
              <div className="mt-2 text-[13px] text-muted-foreground">{tier.desc}</div>
              <ul className="mt-5 space-y-2 text-[13.5px] flex-1">
                {tier.feats.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check className="h-3.5 w-3.5 mt-1 shrink-0" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={tier.name === "Enterprise" ? "/contact" : "/auth"}
                className={`mt-7 inline-flex justify-center items-center rounded-full px-4 py-2.5 text-[13px] font-medium transition ${
                  tier.featured
                    ? "bg-foreground text-background hover:opacity-90"
                    : "hairline bg-background hover:bg-cream"
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 text-[13.5px] text-muted-foreground hover:text-foreground transition"
          >
            {t("home.pricing.compare")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ============================ FAQ ============================ */
function FAQ() {
  const t = useHydratedTranslation();
  const faqs = t("home.faq.items", { returnObjects: true }) as { q: string; a: string }[];
  return (
    <section className="bg-cream border-y border-border py-24 sm:py-28">
      <div className="mx-auto max-w-4xl px-6">
        <SectionHeader
          eyebrow={t("home.faq.eyebrow")}
          title={
            <>
              {t("home.faq.titleA")}{" "}
              <em className="italic text-muted-foreground">{t("home.faq.titleEm")}</em>
            </>
          }
        />
        <div className="mt-12 border-t border-border">
          {faqs.map((f) => (
            <details
              key={f.q}
              className="group border-b border-border [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer items-center justify-between py-5 text-[16px] sm:text-[17px] font-medium gap-4">
                <span className="min-w-0">{f.q}</span>
                <span className="ml-2 text-muted-foreground shrink-0">
                  <Plus className="h-4 w-4 group-open:hidden" />
                  <Minus className="h-4 w-4 hidden group-open:block" />
                </span>
              </summary>
              <p className="pb-5 text-[14.5px] leading-relaxed text-muted-foreground max-w-xl">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================ CTA ============================ */
function CTA() {
  const t = useHydratedTranslation();
  return (
    <section className="py-24 sm:py-28">
      <div className="mx-auto max-w-5xl px-6">
        <div className="hairline rounded-2xl bg-card p-10 sm:p-16 md:p-20 text-center">
          <Eyebrow>{t("home.cta.eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif text-[36px] sm:text-[56px] md:text-[64px] leading-[1.02] tracking-[-0.03em]">
            {t("home.cta.titleA")}{" "}
            <em className="italic text-muted-foreground">{t("home.cta.titleEm")}</em>
          </h2>
          <p className="mt-5 text-[15px] sm:text-[16px] text-muted-foreground max-w-md mx-auto">
            {t("home.cta.sub")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <Link
              to="/auth"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition"
            >
              {t("home.cta.primary")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/docs"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-1.5 rounded-full hairline bg-background px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
            >
              {t("home.cta.secondary")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================ PAGE ============================ */
function Landing() {
  return (
    <MarketingLayout>
      <Hero />
      <WhatIsRTN />
      <LogoStrip />
      <HowItWorks />
      <Features />
      <LiveDemo />
      <ReachableInAI />
      <UseCases />
      <Testimonials />
      <PricingPreview />
      <FAQ />
      <CTA />
    </MarketingLayout>
  );
}
